// Mission.js
import React from "react";
import { FiCheckCircle, FiEye } from "react-icons/fi";
import { motion } from "framer-motion";

const fadeUp = {
  hidden: { opacity: 0, y: 60 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const fadeLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const fadeRight = {
  hidden: { opacity: 0, x: 60 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.15 },
  },
};

const Mission = () => {
  const missionPoints = [
    "Generational Craftsmanship & Integrity",
    "Complex Projects Done Right",
    "A-to-Z Project Planning & Execution",
    "High-Level Oversight & Process-Driven Systems",
    "Trusted by Homeowners, HOAs & Investors",
  ];

  const visionPoints = [
    "Attracting High-Value Projects Across California",
    "Building Authority in HOA Reconstruction",
    "Expanding Strategic Commercial Services",
    "Delivering Peace of Mind on Every Build",
    "Growing a Legacy Brand Built on Competence",
  ];

  return (
    <section id="mission-section" className="bg-[#f8f9fa] py-12 px-6">
      <div className="max-w-4xl mx-auto">
        <div className="grid md:grid-cols-2 gap-8">

          {/* Our Mission — slides in from left */}
          <motion.div
            className="bg-white rounded-xl shadow-md border border-[#dee2e6]
                        flex flex-col items-center text-center p-8
                        transition-all duration-500 transform hover:scale-105
                        hover:bg-[#212529] hover:shadow-[0_0_25px_rgba(0,0,0,0.2)] group"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0 }}
          >
            <motion.div
              variants={fadeLeft}
              className="w-16 h-16 mb-4 rounded-full flex items-center justify-center
                         bg-[#343a40] group-hover:bg-white transition-colors duration-500"
            >
              <FiCheckCircle className="text-[#f8f9fa] group-hover:text-[#343a40] w-8 h-8 transition-colors duration-500" />
            </motion.div>
            <motion.h2
              variants={fadeLeft}
              className="text-2xl md:text-3xl font-bold mb-3 text-[#212529] group-hover:text-white transition-colors duration-500"
            >
              Our Mission
            </motion.h2>
            <motion.p
              variants={fadeLeft}
              className="text-sm text-[#6c757d] leading-relaxed mb-6 group-hover:text-white transition-colors duration-500"
            >
              To protect homeowners, investors, and HOAs from bad construction
              experiences — delivering complex, high-value projects from rough
              to perfect through integrity, structure, and third-generation
              craftsmanship.
            </motion.p>
            <motion.div className="space-y-3 inline-block text-left" variants={staggerContainer}>
              {missionPoints.map((point, idx) => (
                <motion.div key={idx} variants={fadeUp} className="flex items-start gap-3">
                  <div className="w-5 h-5 mt-0.5 rounded-full flex items-center justify-center flex-shrink-0
                                  bg-[#343a40] group-hover:bg-white transition-colors duration-500">
                    <svg className="w-3 h-3 text-[#f8f9fa] group-hover:text-[#343a40] transition-colors duration-500" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M16.707 5.293a1 1 0 010 1.414L8.414 15l-4.121-4.121a1 1 0 011.414-1.414L8.414 12.172l6.879-6.879a1 1 0 011.414 0z" />
                    </svg>
                  </div>
                  <p className="text-sm md:text-base text-[#495057] font-medium group-hover:text-white transition-colors duration-500">{point}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Our Vision — slides in from right */}
          <motion.div
            className="bg-white rounded-xl shadow-md border border-[#dee2e6]
                        flex flex-col items-center text-center p-8
                        transition-all duration-500 transform hover:scale-105
                        hover:bg-[#212529] hover:shadow-[0_0_25px_rgba(0,0,0,0.2)] group"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0 }}
          >
            <motion.div
              variants={fadeRight}
              className="w-16 h-16 mb-4 rounded-full flex items-center justify-center
                         bg-[#343a40] group-hover:bg-white transition-colors duration-500"
            >
              <FiEye className="text-[#f8f9fa] group-hover:text-[#343a40] w-8 h-8 transition-colors duration-500" />
            </motion.div>
            <motion.h2
              variants={fadeRight}
              className="text-2xl md:text-3xl font-bold mb-3 text-[#212529] group-hover:text-white transition-colors duration-500"
            >
              Our Vision
            </motion.h2>
            <motion.p
              variants={fadeRight}
              className="text-sm text-[#6c757d] leading-relaxed mb-6 group-hover:text-white transition-colors duration-500"
            >
              To be California's most trusted high-value construction firm —
              recognized for competence, experience, and structure. We are not
              competing on price. We compete on results that stand for
              generations.
            </motion.p>
            <motion.div className="space-y-3 inline-block text-left" variants={staggerContainer}>
              {visionPoints.map((point, idx) => (
                <motion.div key={idx} variants={fadeUp} className="flex items-start gap-3">
                  <div className="w-5 h-5 mt-0.5 rounded-full flex items-center justify-center flex-shrink-0
                                 bg-[#212529] group-hover:bg-white transition-colors duration-500">
                    <svg className="w-3 h-3 text-[#f8f9fa] group-hover:text-[#343a40] transition-colors duration-500" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M16.707 5.293a1 1 0 010 1.414L8.414 15l-4.121-4.121a1 1 0 011.414-1.414L8.414 12.172l6.879-6.879a1 1 0 011.414 0z" />
                    </svg>
                  </div>
                  <p className="text-sm md:text-base text-[#495057] font-medium group-hover:text-white transition-colors duration-500">{point}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default Mission;